/*!
 * TinyPoker AOF 分析組件
 * 顯示 All-in or Fold 玩家數據分析
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Progress,
} from "reactstrap";
import axios from "axios";
import classnames from "classnames";

import TinyPageHeader from "components/tiny/TinyPageHeader";
import TinyPageTabs from "components/tiny/TinyPageTabs";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
const API_BASE = "/api/tiny";

function TinyAOFAnalysis() {
  // State
  const [weeks, setWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  const [players, setPlayers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [clubStats, setClubStats] = useState([]);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters
  const [minHands, setMinHands] = useState(100);
  const [onlyPartners, setOnlyPartners] = useState(false);
  const [activeTab, setActiveTab] = useState("players");

  // 載入週列表
  const loadWeeks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/data/weeks`);
      if (res.data.code === 1) {
        const allWeeks = res.data.data.weeks || [];
        setWeeks(allWeeks);
        // 默認選擇最近 4 週
        setSelectedWeeks(allWeeks.slice(0, 4));
      }
    } catch (err) {
      console.error("載入週列表失敗:", err);
    }
  }, []);

  // 載入 AOF 玩家數據
  const loadAOFPlayers = useCallback(async () => {
    if (selectedWeeks.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      params.append("weeks", selectedWeeks.join(","));
      params.append("min_hands", minHands);
      if (onlyPartners) params.append("only_partners", "true");
      params.append("limit", 200);
      
      const res = await axios.get(`${API_BASE}/aof/players?${params}`);
      if (res.data.code === 1) {
        setPlayers(res.data.data.players || []);
        setSummary(res.data.data.summary || null);
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      setError("載入 AOF 數據失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, [selectedWeeks, minHands, onlyPartners]);

  // 載入俱樂部統計
  const loadClubStats = useCallback(async () => {
    if (selectedWeeks.length === 0) return;
    
    try {
      const res = await axios.get(`${API_BASE}/aof/clubs?week=${selectedWeeks[0]}`);
      if (res.data.code === 1) {
        setClubStats(res.data.data.clubs || []);
      }
    } catch (err) {
      console.error("載入俱樂部統計失敗:", err);
    }
  }, [selectedWeeks]);

  // 載入趨勢
  const loadTrend = useCallback(async () => {
    if (selectedWeeks.length === 0) return;
    
    try {
      const params = new URLSearchParams();
      params.append("weeks", selectedWeeks.join(","));
      
      const res = await axios.get(`${API_BASE}/aof/trend?${params}`);
      if (res.data.code === 1) {
        setTrend(res.data.data.trend || []);
      }
    } catch (err) {
      console.error("載入趨勢失敗:", err);
    }
  }, [selectedWeeks]);

  // 初始載入
  useEffect(() => {
    loadWeeks();
  }, [loadWeeks]);

  // 當選擇週期改變時載入資料
  useEffect(() => {
    if (selectedWeeks.length > 0) {
      loadAOFPlayers();
      loadClubStats();
      loadTrend();
    }
  }, [selectedWeeks, loadAOFPlayers, loadClubStats, loadTrend]);

  // 格式化數字
  const formatNumber = (num) => {
    if (num === null || num === undefined) return "-";
    return parseFloat(num).toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  // 格式化金額（帶正負號）
  const formatPL = (num) => {
    const val = parseFloat(num) || 0;
    const prefix = val >= 0 ? "+" : "";
    return `${prefix}$${formatNumber(Math.abs(val))}`;
  };

  // 切換週選擇
  const toggleWeek = (week) => {
    if (selectedWeeks.includes(week)) {
      setSelectedWeeks(selectedWeeks.filter(w => w !== week));
    } else {
      setSelectedWeeks([...selectedWeeks, week].sort().reverse());
    }
  };

  return (
    <div className="content">
      {/* 這頁原本沒有 page header（「篩選條件」是篩選卡的標題）。
          ⚠️ tabs 放在 KPI 卡之後（KPI 是頁面層級的），所以用 standalone
             的 TinyPageTabs 留在原位，不跟著 header。 */}
      <TinyPageHeader
        title="AOF 分析"
        description="All-in or Fold 桌型的玩家與俱樂部表現，可多選週期比較。"
      />

      {error && (
        <Alert color="danger" toggle={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 篩選區 */}
      <Card className="mb-3">
        <CardHeader>
          <CardTitle tag="h5">
            <i className="fa fa-filter mr-2" />
            篩選條件
          </CardTitle>
        </CardHeader>
        <CardBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>週期選擇（可多選）</Label>
                <div className="d-flex flex-wrap">
                  {weeks.map((week) => (
                    <Badge
                      key={week}
                      color={selectedWeeks.includes(week) ? "primary" : "light"}
                      className="mr-2 mb-2"
                      style={{ cursor: "pointer", fontSize: "0.9em" }}
                      onClick={() => toggleWeek(week)}
                    >
                      {week}
                    </Badge>
                  ))}
                </div>
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup>
                <Label>最低手數</Label>
                <Input
                  type="number"
                  value={minHands}
                  onChange={(e) => setMinHands(parseInt(e.target.value) || 0)}
                  min={0}
                />
              </FormGroup>
            </Col>
            <Col md={2}>
              <FormGroup check className="mt-4">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={onlyPartners}
                    onChange={(e) => setOnlyPartners(e.target.checked)}
                  />{" "}
                  只顯示合作夥伴
                </Label>
              </FormGroup>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button color="primary" onClick={loadAOFPlayers} disabled={loading}>
                {loading ? <TinyBusy light style={{ marginRight: 4 }} /> : <i className="fa fa-search mr-1" />}
                查詢
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* 統計卡片 */}
      {summary && (
        <Row className="mb-3">
          <Col md={2}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center">
                      <i className="fa fa-users text-primary" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">AOF 玩家</p>
                      <CardTitle tag="p">{formatNumber(summary.total_players)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center">
                      <i className="fa fa-star text-warning" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">合作夥伴</p>
                      <CardTitle tag="p">{formatNumber(summary.partner_players)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center">
                      <i className="fa fa-hand text-info" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">AFH 手數</p>
                      <CardTitle tag="p">{formatNumber(summary.total_afh_hands)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center">
                      <i className="fa fa-hand-back-fist text-success" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">AFO 手數</p>
                      <CardTitle tag="p">{formatNumber(summary.total_afo_hands)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center">
                      <i className="fa fa-usd text-success" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">總服務費</p>
                      <CardTitle tag="p">${formatNumber(summary.total_rake)}</CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
          <Col md={2}>
            <Card className="card-stats">
              <CardBody>
                <Row>
                  <Col xs={5}>
                    <div className="icon-big text-center">
                      <i className="fa fa-line-chart text-danger" />
                    </div>
                  </Col>
                  <Col xs={7}>
                    <div className="numbers">
                      <p className="card-category">總 P&L</p>
                      <CardTitle tag="p" className={summary.total_pl >= 0 ? "text-success" : "text-danger"}>
                        {formatPL(summary.total_pl)}
                      </CardTitle>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      <TinyPageTabs
        items={[
          { key: "players", label: "玩家列表", icon: "fa fa-users", count: players.length },
          { key: "clubs", label: "俱樂部統計", icon: "fa fa-building" },
          { key: "trend", label: "趨勢分析", icon: "fa fa-line-chart" },
        ]}
        active={activeTab}
        onChange={setActiveTab}
      />

      <TabContent activeTab={activeTab}>
        {/* 玩家列表 Tab */}
        <TabPane tabId="players">
          <Card>
            <CardBody>
              {loading && players.length === 0 ? (
                <TinyLoading variant="block" text="搜尋中" />
              ) : players.length === 0 ? (
                <div className="text-center p-5">
                  <i className="fa fa-inbox fa-3x text-muted mb-3" />
                  <p className="text-muted">無符合條件的 AOF 玩家</p>
                </div>
              ) : (
                <TinyLoadingOverlay active={loading} text="搜尋中">
                <Table responsive hover size="sm">
                  <thead className="text-primary">
                    <tr>
                      <th>#</th>
                      <th>玩家 ID</th>
                      <th>暱稱</th>
                      <th>俱樂部</th>
                      <th>AFH 手數</th>
                      <th>AFO 手數</th>
                      <th>總手數</th>
                      <th>總服務費</th>
                      <th>P&L</th>
                      <th>合作夥伴</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map((player, idx) => (
                      <tr key={player.user_id + idx}>
                        <td>{idx + 1}</td>
                        <td><code>{player.user_id}</code></td>
                        <td>{player.username || "-"}</td>
                        <td>
                          <small>{player.club_name || player.club_id}</small>
                        </td>
                        <td>{formatNumber(player.afh_hands)}</td>
                        <td>{formatNumber(player.afo_hands)}</td>
                        <td><strong>{formatNumber(player.total_hands)}</strong></td>
                        <td>${formatNumber(player.total_rake)}</td>
                        <td className={player.total_pl >= 0 ? "text-success" : "text-danger"}>
                          {formatPL(player.total_pl)}
                        </td>
                        <td>
                          {player.is_partner ? (
                            <Badge color="warning" title={player.partner_info}>
                              <i className="fa fa-star" />
                            </Badge>
                          ) : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                </TinyLoadingOverlay>
              )}
            </CardBody>
          </Card>
        </TabPane>

        {/* 俱樂部統計 Tab */}
        <TabPane tabId="clubs">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">
                俱樂部 AOF 統計 ({selectedWeeks[0] || "請選擇週期"})
              </CardTitle>
            </CardHeader>
            <CardBody>
              {clubStats.length === 0 ? (
                <p className="text-muted text-center">無資料</p>
              ) : (
                <Table responsive hover>
                  <thead className="text-primary">
                    <tr>
                      <th>俱樂部</th>
                      <th>玩家數</th>
                      <th>AFH 手數</th>
                      <th>AFO 手數</th>
                      <th>總手數</th>
                      <th>總服務費</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubStats.map((club, idx) => (
                      <tr key={club.club_id || idx}>
                        <td>{club.club_name || club.club_id}</td>
                        <td>{formatNumber(club.player_count)}</td>
                        <td>{formatNumber(club.total_afh_hands)}</td>
                        <td>{formatNumber(club.total_afo_hands)}</td>
                        <td><strong>{formatNumber(club.total_hands)}</strong></td>
                        <td>${formatNumber(club.total_rake)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </CardBody>
          </Card>
        </TabPane>

        {/* 趨勢分析 Tab */}
        <TabPane tabId="trend">
          <Card>
            <CardHeader>
              <CardTitle tag="h5">
                <i className="fa fa-line-chart mr-2" />
                AOF 週度趨勢
              </CardTitle>
            </CardHeader>
            <CardBody>
              {trend.length === 0 ? (
                <p className="text-muted text-center">無資料</p>
              ) : (
                <>
                  <Table responsive hover>
                    <thead className="text-primary">
                      <tr>
                        <th>週期</th>
                        <th>玩家數</th>
                        <th>AFH 手數</th>
                        <th>AFO 手數</th>
                        <th>總手數</th>
                        <th>總服務費</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trend.map((t, idx) => (
                        <tr key={t.week || idx}>
                          <td><strong>{t.week}</strong></td>
                          <td>{formatNumber(t.player_count)}</td>
                          <td>{formatNumber(t.afh_hands)}</td>
                          <td>{formatNumber(t.afo_hands)}</td>
                          <td>{formatNumber(t.total_hands)}</td>
                          <td>${formatNumber(t.total_rake)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  
                  {/* 簡易趨勢圖 */}
                  <div className="mt-4">
                    <h6>手數趨勢</h6>
                    {trend.map((t, idx) => {
                      const maxHands = Math.max(...trend.map(x => x.total_hands || 0));
                      const pct = maxHands ? ((t.total_hands || 0) / maxHands * 100) : 0;
                      return (
                        <div key={t.week + idx} className="mb-2">
                          <div className="d-flex justify-content-between mb-1">
                            <small>{t.week}</small>
                            <small>{formatNumber(t.total_hands)}</small>
                          </div>
                          <Progress value={pct} color="info" />
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </TabPane>
      </TabContent>
    </div>
  );
}

export default TinyAOFAnalysis;


